import { getSupabaseClient } from '../../../../core/supabase/client.js';
import type { UserDto } from '../dtos/UserDto.js';

export interface UserRemoteListOptions {
  limit?: number;
  offset?: number;
}

export interface OrgMembershipRow {
  id?: string;
  role_id?: string;
  created_at?: string;
  role?: { id: string; name: string } | null;
}

export interface ProjectMembershipRow {
  id?: string;
  project_id?: string;
  role_id?: string;
  created_at?: string;
  role?: { id: string; name: string } | null;
  project?: { id: string; name: string } | null;
}

export interface OrgSummaryRow {
  id?: string;
  name?: string;
  description?: string | null;
  created_at?: string;
}

export interface PermissionRuleRow {
  resource?: string;
  action?: string;
  effect?: 'allow' | 'deny' | string;
}

export interface UserRemoteUpsertPayload {
  id?: string;
  uid?: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  displayName?: string | null;
}

export interface UserRemoteUpdatePayload {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  displayName?: string | null;
  provinceCode?: string | null;
  birthDate?: string | null;
  gender?: string | null;
  permanentAddress?: string | null;
  contactAddress?: string | null;
  idCardNumber?: string | null;
  taxCode?: string | null;
  bankAccountNumber?: string | null;
  bankName?: string | null;
  bankBranch?: string | null;
  heightCm?: number | null;
  weightKg?: number | null;
  workExperiences?: string[] | null;
  avatarUrl?: string | null;
  idCardFrontUrl?: string | null;
  idCardBackUrl?: string | null;
  photos?: string[] | null;
}

const HR_FIELDS =
  'province_code, birth_date, gender, permanent_address, contact_address, id_card_number, tax_code, bank_account_number, bank_name, bank_branch, height_cm, weight_kg, work_experiences, avatar_url, id_card_front_url, id_card_back_url, photos';

function mapHrFields(u: Record<string, unknown>) {
  return {
    provinceCode: (u.province_code as string | null) ?? null,
    birthDate: (u.birth_date as string | null) ?? null,
    gender: (u.gender as string | null) ?? null,
    permanentAddress: (u.permanent_address as string | null) ?? null,
    contactAddress: (u.contact_address as string | null) ?? null,
    idCardNumber: (u.id_card_number as string | null) ?? null,
    taxCode: (u.tax_code as string | null) ?? null,
    bankAccountNumber: (u.bank_account_number as string | null) ?? null,
    bankName: (u.bank_name as string | null) ?? null,
    bankBranch: (u.bank_branch as string | null) ?? null,
    heightCm: (u.height_cm as number | null) ?? null,
    weightKg: (u.weight_kg as number | null) ?? null,
    workExperiences: (u.work_experiences as string[] | null) ?? null,
    avatarUrl: (u.avatar_url as string | null) ?? null,
    idCardFrontUrl: (u.id_card_front_url as string | null) ?? null,
    idCardBackUrl: (u.id_card_back_url as string | null) ?? null,
    photos: (u.photos as string[] | null) ?? null,
  };
}

export class UserRemoteDataSource {
  async getUserById(uid: string): Promise<UserDto | null> {
    const supabase = getSupabaseClient() as any;
    const { data, error } = await supabase
      .from('profiles')
      .select(`id, first_name, last_name, email, phone, created_at, updated_at, is_super_admin, ${HR_FIELDS}`)
      .eq('id', uid)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;

    const u = data;
    const firstName = u.first_name ?? '';
    const lastName = u.last_name ?? '';
    return {
      id: u.id,
      uid: u.id,
      firstName,
      lastName,
      email: u.email,
      phone: u.phone ?? null,
      displayName: `${firstName} ${lastName}`.trim() || null,
      createdAt: u.created_at ?? null,
      updatedAt: u.updated_at ?? null,
      isSuperAdmin: u.is_super_admin ?? false,
      ...mapHrFields(u),
    };
  }

  async getUsersByIds(ids: string[]): Promise<UserDto[]> {
    if (!ids.length) return [];
    const supabase = getSupabaseClient() as any;
    const { data, error } = await supabase
      .from('profiles')
      .select(`id, first_name, last_name, email, phone, created_at, updated_at, ${HR_FIELDS}`)
      .in('id', ids);
    if (error) throw new Error(error.message);
    return (data ?? []).map((u: any) => {
      const firstName = u.first_name ?? '';
      const lastName = u.last_name ?? '';
      return {
        id: u.id,
        uid: u.id,
        firstName,
        lastName,
        email: u.email ?? '',
        phone: u.phone ?? null,
        displayName: `${firstName} ${lastName}`.trim() || null,
        createdAt: u.created_at ?? null,
        updatedAt: u.updated_at ?? null,
        ...mapHrFields(u),
      };
    });
  }

  async getUsersByEmails(emails: string[]): Promise<UserDto[]> {
    const cleanEmails = [...new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean))];
    if (!cleanEmails.length) return [];
    const supabase = getSupabaseClient() as any;
    const orClause = cleanEmails.map((e) => `email.ilike.${e}`).join(',');
    const { data, error } = await supabase
      .from('profiles')
      .select(`id, first_name, last_name, email, phone, created_at, updated_at, ${HR_FIELDS}`)
      .or(orClause);
    if (error) throw new Error(error.message);
    return (data ?? []).map((u: any) => {
      const firstName = u.first_name ?? '';
      const lastName = u.last_name ?? '';
      return {
        id: u.id,
        uid: u.id,
        firstName,
        lastName,
        email: u.email ?? '',
        phone: u.phone ?? null,
        displayName: `${firstName} ${lastName}`.trim() || null,
        createdAt: u.created_at ?? null,
        updatedAt: u.updated_at ?? null,
        ...mapHrFields(u),
      };
    });
  }

  async listUsers(options?: UserRemoteListOptions): Promise<{ data: UserDto[]; total: number }> {
    const supabase = getSupabaseClient() as any;
    let query = supabase
      .from('profiles')
      .select('id, first_name, last_name, email, phone, created_at, updated_at', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (typeof options?.offset === 'number' && typeof options?.limit === 'number') {
      query = query.range(options.offset, options.offset + options.limit - 1);
    } else if (typeof options?.limit === 'number') {
      query = query.limit(options.limit);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(error.message);

    const mapped = (data ?? []).map((u: any) => {
      const firstName = u.first_name ?? '';
      const lastName = u.last_name ?? '';
      return {
        id: u.id,
        uid: u.id,
        firstName,
        lastName,
        email: u.email,
        phone: u.phone ?? null,
        displayName: `${firstName} ${lastName}`.trim() || null,
        createdAt: u.created_at ?? null,
        updatedAt: u.updated_at ?? null,
      };
    });
    return { data: mapped, total: count ?? 0 };
  }

  async upsertUser(payload: UserRemoteUpsertPayload, ignoreDuplicates = false): Promise<void> {
    const nameFromDisplay = (payload.displayName ?? '').trim();
    const [fallbackFirstName = '', ...restNameParts] = nameFromDisplay.split(/\s+/).filter(Boolean);
    const fallbackLastName = restNameParts.join(' ');

    const supabase = getSupabaseClient() as any;
    const { error } = await supabase.from('profiles').upsert({
      id: payload.id ?? payload.uid,
      first_name: payload.firstName ?? fallbackFirstName,
      last_name: payload.lastName ?? fallbackLastName,
      email: payload.email,
      phone: payload.phone ?? null,
    }, { ignoreDuplicates });
    if (error) throw new Error(error.message);
  }

  async updateUser(uid: string, payload: UserRemoteUpdatePayload): Promise<boolean> {
    const supabase = getSupabaseClient() as any;
    const updateData: Record<string, unknown> = {};
    if (payload.firstName !== undefined) updateData.first_name = payload.firstName;
    if (payload.lastName !== undefined) updateData.last_name = payload.lastName;
    if (payload.email !== undefined) updateData.email = payload.email;
    if (payload.phone !== undefined) updateData.phone = payload.phone;
    if (payload.provinceCode !== undefined) updateData.province_code = payload.provinceCode;
    if (payload.birthDate !== undefined) updateData.birth_date = payload.birthDate;
    if (payload.gender !== undefined) updateData.gender = payload.gender;
    if (payload.permanentAddress !== undefined) updateData.permanent_address = payload.permanentAddress;
    if (payload.contactAddress !== undefined) updateData.contact_address = payload.contactAddress;
    if (payload.idCardNumber !== undefined) updateData.id_card_number = payload.idCardNumber;
    if (payload.taxCode !== undefined) updateData.tax_code = payload.taxCode;
    if (payload.bankAccountNumber !== undefined) updateData.bank_account_number = payload.bankAccountNumber;
    if (payload.bankName !== undefined) updateData.bank_name = payload.bankName;
    if (payload.bankBranch !== undefined) updateData.bank_branch = payload.bankBranch;
    if (payload.heightCm !== undefined) updateData.height_cm = payload.heightCm;
    if (payload.weightKg !== undefined) updateData.weight_kg = payload.weightKg;
    if (payload.workExperiences !== undefined) updateData.work_experiences = payload.workExperiences;
    if (payload.avatarUrl !== undefined) updateData.avatar_url = payload.avatarUrl;
    if (payload.idCardFrontUrl !== undefined) updateData.id_card_front_url = payload.idCardFrontUrl;
    if (payload.idCardBackUrl !== undefined) updateData.id_card_back_url = payload.idCardBackUrl;
    if (payload.photos !== undefined) updateData.photos = payload.photos;
    if (Object.keys(updateData).length === 0) return false;
    const { error, data } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', uid)
      .select('id')
      .maybeSingle();
    if (error) throw new Error(error.message);
    return !!data?.id;
  }

  async deleteUser(uid: string): Promise<void> {
    const supabase = getSupabaseClient() as any;
    const { error } = await supabase.from('profiles').delete().eq('id', uid);
    if (error) throw new Error(error.message);
  }

  async searchAllProfiles(query: string, limit = 20): Promise<UserDto[]> {
    const supabase = getSupabaseClient() as any;
    const q = query.trim();
    let req = supabase
      .from('profiles')
      .select('id, first_name, last_name, email, phone, created_at, updated_at, is_super_admin')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (q) {
      req = req.or(`email.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%`);
    }
    const { data, error } = await req;
    if (error) throw new Error(error.message);
    return (data ?? []).map((u: any) => {
      const firstName = u.first_name ?? '';
      const lastName = u.last_name ?? '';
      return {
        id: u.id,
        uid: u.id,
        firstName,
        lastName,
        email: u.email,
        phone: u.phone ?? null,
        displayName: `${firstName} ${lastName}`.trim() || null,
        createdAt: u.created_at ?? null,
        updatedAt: u.updated_at ?? null,
        isSuperAdmin: u.is_super_admin ?? false,
      };
    });
  }

  async getOrgMembership(profileId: string, orgId: string): Promise<OrgMembershipRow | null> {
    const supabase = getSupabaseClient() as any;
    const { data, error } = await supabase
      .from('memberships')
      .select('id, role_id, created_at, role:roles(id, name)')
      .eq('profile_id', profileId)
      .eq('org_id', orgId)
      .is('project_id', null)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (data as OrgMembershipRow | null) ?? null;
  }

  async getProjectMemberships(profileId: string, orgId: string): Promise<ProjectMembershipRow[]> {
    const supabase = getSupabaseClient() as any;
    const { data, error } = await supabase
      .from('memberships')
      .select('id, project_id, role_id, created_at, role:roles(id, name), project:projects(id, name)')
      .eq('profile_id', profileId)
      .eq('org_id', orgId)
      .not('project_id', 'is', null)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data as ProjectMembershipRow[]) ?? [];
  }

  async getMyOrgs(profileId: string): Promise<OrgSummaryRow[]> {
    const supabase = getSupabaseClient() as any;
    const { data, error } = await supabase.rpc('get_orgs_of_profile', {
      p_profile_id: profileId,
      row_limit: 50,
      row_offset: 0,
    });
    if (error) throw new Error(error.message);
    return (data as OrgSummaryRow[]) ?? [];
  }

  async getMyPermissions(orgId: string): Promise<PermissionRuleRow[]> {
    const supabase = getSupabaseClient() as any;
    const { data, error } = await supabase.rpc('get_my_permission_rules', {
      p_org_id: orgId,
      p_project_id: null,
    });
    if (error) throw new Error(error.message);
    return (data as PermissionRuleRow[]) ?? [];
  }

  async getMyProjectPermissions(orgId: string, projectId: string): Promise<PermissionRuleRow[]> {
    const supabase = getSupabaseClient() as any;
    const { data, error } = await supabase.rpc('get_my_permission_rules', {
      p_org_id: orgId,
      p_project_id: projectId,
    });
    if (error) throw new Error(error.message);
    return (data as PermissionRuleRow[]) ?? [];
  }
}

